import React, { useState } from 'react';
import { getApiUrl, API_ENDPOINTS } from '../config/apiConfig';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { fetchDetailsProxy, fetchSeasonDetailsProxy } from '../api';

const AdminContainer = styled(motion.div)`
  padding: 50px 5%;
  min-height: 100vh;
  background-color: #0b0614;
`;

const Header = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 30px;
`;

const Title = styled.h1`
  font-size: 3rem;
  font-weight: 800;
  background: linear-gradient(90deg, var(--gradient-cyan), var(--gradient-pink));
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const BackButton = styled(Link)`
    padding: 12px 25px;
    font-size: 1rem;
    font-weight: 600;
    border-radius: 10px;
    background: var(--glass-bg);
    color: var(--primary-color);
    border: 1px solid var(--border-color);
    text-decoration: none;
    transition: all 0.3s ease;

    &:hover {
        border-color: var(--gradient-pink);
    }
`;

const SearchBox = styled.form`
  display: flex;
  gap: 15px;
  margin-bottom: 40px;

  input {
    flex-grow: 1;
    padding: 15px 20px;
    font-size: 1.1rem;
    background: var(--glass-bg);
    border: 1px solid var(--border-color);
    border-radius: 10px;
    color: var(--primary-color);
    outline: none;
    transition: border-color 0.3s ease;
    &:focus {
      border-color: var(--gradient-pink);
    }
  }

  button {
    padding: 15px 30px;
    font-size: 1.1rem;
    font-weight: 600;
    border: none;
    border-radius: 10px;
    background: var(--gradient-pink);
    color: white;
    cursor: pointer;
  }
`;

const ResultsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 25px;
`;

const ResultCard = styled.div`
  background: #1a102a;
  border-radius: 10px;
  overflow: hidden;
  border: 1px solid var(--border-color);
  cursor: pointer;
  transition: transform 0.3s ease, border-color 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
    border-color: var(--gradient-cyan);
  }
  
  img {
    width: 100%;
    aspect-ratio: 2/3;
    object-fit: cover;
  }

  .info {
    padding: 15px;
  }

  h4 {
    font-size: 1rem;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    margin-bottom: 5px;
  }

  .media-type {
    font-size: 0.8rem;
    color: var(--gradient-cyan);
    margin-bottom: 10px;
    display: flex;
    align-items: center;
    gap: 5px;
  }

  button {
    width: 100%;
    margin-top: 10px;
    padding: 10px;
    font-size: 0.9rem;
    font-weight: 600;
    border: none;
    border-radius: 8px;
    background: var(--gradient-cyan);
    color: #111;
    cursor: pointer;
    transition: background-color 0.3s ease;

    &:disabled {
      background: #555;
      color: #999;
      cursor: not-allowed;
    }
  }
`;

  const checkIfAnime = async (item) => {
    try {
      if (item.media_type === 'movie') {
        return false;
      }

      const mediaType = item.media_type === 'tv' ? 'tv' : 'movie';
      
      const response = await fetch(`${getApiUrl(API_ENDPOINTS.FETCH_KEYWORDS)}?id=${item.id}&type=${mediaType}`);
      const data = await response.json();
      const keywords = data.keywords || data.results || [];

      const animeKeywords = [
        'anime', 'animation', 'japanese animation', 'manga', 
        'anime series', 'anime adaptation',
        'animasyon', 'japon animasyonu'
      ];
      
      const hasAnimeKeyword = keywords.some(keyword => 
        animeKeywords.some(animeWord => 
          keyword.name?.toLowerCase().includes(animeWord.toLowerCase()) ||
          keyword.name?.toLowerCase().includes('anime')
        )
      );

      const isJapanese = item.original_language === 'ja';
      const isAnimation = item.genre_ids?.includes(16);

      return (hasAnimeKeyword || isJapanese) && isAnimation && item.media_type === 'tv';
    } catch (error) {
      return false;
    }
  };

const saveToMySQL = async (contentData) => {
  try {
    const response = await fetch(getApiUrl(API_ENDPOINTS.CONTENT), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(contentData)
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'MySQL kaydetme hatası');
    }

    const result = await response.json();
    return result;
  } catch (error) {
    console.error('MySQL kaydetme hatası:', error);
    throw error;
  }
};


const fetchSeasonsAndEpisodes = async (tmdbId, mediaType) => {
  if (mediaType === 'movie') return [];
  try {
    const details = await fetchDetailsProxy('tv', tmdbId);
    const totalSeasons = details.number_of_seasons || 1;
    const seasons = [];

    for (let seasonNum = 1; seasonNum <= totalSeasons; seasonNum++) {
      try {
        const seasonData = await fetchSeasonDetailsProxy(tmdbId, seasonNum);
        const today = new Date();
        today.setHours(23, 59, 59, 999);

        const releasedEpisodes = seasonData.episodes.filter(ep => ep.air_date && new Date(ep.air_date) <= today);

        if (releasedEpisodes.length > 0) {
          seasons.push({
            season_number: seasonNum,
            episodes: releasedEpisodes.map(ep => ({
              episode_number: ep.episode_number,
              title: ep.name || `Bölüm ${ep.episode_number}`,
              overview: ep.overview || '',
              air_date: ep.air_date,
              still_path: ep.still_path,
              duration: ep.runtime || ep.duration || null // TMDB'den gelen süre (dakika)
            }))
          });
        }
      } catch (err) {
        
      }
    }

    return seasons;
  } catch (err) {
    console.error('Sezon bilgileri çekilirken hata:', err);
    return [];
  }
};

const checkIfContentExists = async (tmdbId) => {
  try {
    const response = await fetch(getApiUrl(API_ENDPOINTS.CHECK_CONTENT));
    if (!response.ok) throw new Error('İçerikler çekilemedi');
    
    const contents = await response.json();
    return contents.some(content => content.tmdb_id === tmdbId);
  } catch (error) {
    console.error('İçerik kontrol hatası:', error);
    return false;
  }
};

  const AdminAddPage = () => {
    const [searchQuery, setSearchQuery] = useState('');
    const [results, setResults] = useState([]);
    const [addingIds, setAddingIds] = useState([]);
    const [searchLoading, setSearchLoading] = useState(false);

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!searchQuery.trim()) return;

    setSearchLoading(true);

    try {
      const proxyUrl = `${getApiUrl(API_ENDPOINTS.TMDB_SEARCH)}?query=${encodeURIComponent(searchQuery)}`;

      const res = await fetch(proxyUrl);
      const text = await res.text();
      let data;
      try {
        data = JSON.parse(text);
      } catch (jsonError) {
      }

      const filteredResults = data.results.filter(r =>
        (r.media_type === 'movie' || r.media_type === 'tv') && r.poster_path
      );

      const resultsWithMediaType = await Promise.all(
        filteredResults.map(async (item) => {
          const isAnime = await checkIfAnime(item);
          let displayTitle = item.title || item.name;
          
          if (isAnime) {
            try {
              const details = await fetchDetailsProxy('tv', item.id);
              const altTitlesArray = details.alternative_titles?.results || details.alternative_titles?.titles || [];
              
              const romajiTitleObj = altTitlesArray.find(altTitle => {
                return altTitle.type && altTitle.type.toLowerCase() === 'romaji' && altTitle.title;
              });
              
              if (romajiTitleObj) {
                displayTitle = romajiTitleObj.title;
              }
            } catch (error) {
              
            }
          }
          
          return {
            ...item,
            detected_media_type: isAnime ? 'anime' : item.media_type,
            display_title: displayTitle
          };
        })
      );

      setResults(resultsWithMediaType);
    } catch (error) {
      console.error('Arama sırasında hata:', error);
      alert('Arama sırasında hata oluştu!');
    } finally {
      setSearchLoading(false);
    }
  };





const handleAddToSite = async (item) => {
  if (addingIds.includes(item.id)) return;

  const alreadyExists = await checkIfContentExists(item.id);
  if (alreadyExists) {
    alert("Bu içerik zaten siteye eklenmiş!");
    return;
  }

  setAddingIds(prev => [...prev, item.id]);

  try {
    const originalMediaType = item.media_type === 'tv' ? 'tv' : 'movie';
    const details = await fetchDetailsProxy(originalMediaType, item.id);

    const isAnime = await checkIfAnime(item);
    const finalMediaType = isAnime ? 'anime' : originalMediaType;

    let mainTitle = '';
    let secondaryTitle = '';
    let englishTitle = '';
    let turkishTitle = '';
    let romajiTitle = '';
    let japaneseTitle = item.original_title || item.original_name || '';

    if (details.translations?.translations) {
      const englishTranslation = details.translations.translations.find(t => 
        t.iso_639_1 === 'en' && (t.data?.title || t.data?.name)
      );
      if (englishTranslation) {
        englishTitle = englishTranslation.data.title || englishTranslation.data.name;
      }

      const turkishTranslation = details.translations.translations.find(t => 
        t.iso_639_1 === 'tr' && (t.data?.title || t.data?.name)
      );
      if (turkishTranslation) {
        turkishTitle = turkishTranslation.data.title || turkishTranslation.data.name;
      }
    }

    if (finalMediaType === 'anime') {
      const altTitlesArray = details.alternative_titles?.results || details.alternative_titles?.titles || [];
      
      if (altTitlesArray.length > 0) {
        const romajiTitleObj = altTitlesArray.find(altTitle => {
          const isRomaji = altTitle.type && altTitle.type.toLowerCase() === 'romaji';
          return isRomaji && altTitle.title;
        });
        
        if (romajiTitleObj) {
          romajiTitle = romajiTitleObj.title;
        }
      }
    }

    if (item.original_language === 'en') {
      englishTitle = japaneseTitle;
    }

    // Japonca karakter tespiti için regex
    const japaneseRegex = /[\u3040-\u309F\u30A0-\u30FF\u4E00-\u9FAF]/;
    
    // İngilizce (Latin karakterler) kontrolü
    const isEnglish = (str) => {
      if (!str || str.trim() === '') return false;
      // Sadece Latin karakterler, rakamlar, boşluk ve yaygın noktalama işaretleri
      return /^[a-zA-Z0-9\s\-'.,!?()&:;]+$/.test(str);
    };

    if (finalMediaType === 'anime') {
      const currentTitle = item.title || item.name || '';
      
      // Eğer romanji yoksa ve currentTitle Japonca karakterler içeriyorsa
      if (!romajiTitle && currentTitle && japaneseRegex.test(currentTitle)) {
        // Eğer englishTitle İngilizce ise, swap yap
        if (isEnglish(englishTitle) && englishTitle.trim() !== '') {
          mainTitle = englishTitle;
          secondaryTitle = currentTitle; // Japonca karakterler
        } else {
          // EnglishTitle yoksa veya İngilizce değilse, normal mantık
          mainTitle = japaneseTitle || currentTitle;
          secondaryTitle = englishTitle || currentTitle;
        }
      } else {
        // Romanji varsa veya currentTitle Japonca değilse normal mantık
        mainTitle = romajiTitle || japaneseTitle || currentTitle;
        secondaryTitle = englishTitle || currentTitle;
      }
    } else {
      mainTitle = englishTitle || (item.title || item.name) || japaneseTitle;
      secondaryTitle = turkishTitle || (item.title || item.name);
    }

    const alternateTitles = [];
    
    if ((item.title || item.name) && (item.title || item.name) !== mainTitle && (item.title || item.name) !== secondaryTitle) {
      alternateTitles.push(item.title || item.name);
    }

    if (details.translations?.translations) {
      details.translations.translations.forEach(t => {
        const title = t.data?.title || t.data?.name;
        if (title && 
            title !== mainTitle && 
            title !== secondaryTitle && 
            !alternateTitles.includes(title)) {
          alternateTitles.push(title);
        }
      });
    }

    if (details.alternative_titles?.titles) {
      details.alternative_titles.titles.forEach(altTitle => {
        if (altTitle.title && 
            altTitle.title !== mainTitle && 
            altTitle.title !== secondaryTitle && 
            !alternateTitles.includes(altTitle.title)) {
          alternateTitles.push(altTitle.title);
        }
      });
    }

    const categories = details.genres?.map(g => g.name) || [];

    let seasonsData = [];
    if (finalMediaType === 'tv' || finalMediaType === 'anime') {
      seasonsData = await fetchSeasonsAndEpisodes(item.id, originalMediaType);
    }

    const mysqlData = {
      content: {
        tmdb_id: item.id,
        title: mainTitle,
        original_title: secondaryTitle,
        media_type: finalMediaType,
        overview: item.overview || details.overview,
        poster_path: item.poster_path,
        backdrop_path: item.backdrop_path,
        release_date: item.release_date || item.first_air_date,
        vote_average: item.vote_average
      },
      alternate_titles: alternateTitles,
      categories,
      seasons: seasonsData.map(season => ({
        season_number: season.season_number,
        episodes: season.episodes.map(ep => ({
          episode_number: ep.episode_number,
          episode_title: ep.title,
          episode_overview: ep.overview,
          thumbnail_path: ep.still_path,
          air_date: ep.air_date,
          duration: ep.duration || null // Bölüm süresi (dakika)
        }))
      }))
    };

    const result = await saveToMySQL(mysqlData);
    alert(`"${mysqlData.content.title}" başarıyla siteye eklendi! (Tür: ${finalMediaType})`);

  } catch (error) {
    console.error("İçerik eklenirken hata oluştu:", error);
    alert(`Hata: ${error.message}`);
  } finally {
    setAddingIds(prev => prev.filter(id => id !== item.id));
  }
};

  const getImageUrl = (path, size = 'w500') => {
    if (!path) return '/placeholder-image.jpg';
    
    if (path.includes('/proxy-image.php')) return path;

    return `${getApiUrl(API_ENDPOINTS.PROXY_IMAGE)}?path=${encodeURIComponent(path)}&size=${size}`;
  };

  const getMediaTypeInfo = (mediaType) => {
    switch (mediaType) {
      case 'anime':
        return { color: '#ff6b6b', label: 'Anime', icon: '' };
      case 'tv':
        return { color: '#4ecdc4', label: 'Dizi', icon: '' };
      case 'movie':
        return { color: '#45b7d1', label: 'Film', icon: '' };
      default:
        return { color: '#96ceb4', label: mediaType, icon: '' };
    }
  };

  return (
    <AdminContainer initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
      <Header>
        <Title>Yeni İçerik Ekle</Title>
        <BackButton to="/admin">Panele Dön</BackButton>
      </Header>
      
      <SearchBox onSubmit={handleSearch}>
        <input 
          type="text"
          placeholder="TMDB'de film, dizi veya anime ara..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
        <button type="submit" disabled={searchLoading}>
          {searchLoading ? 'Aranıyor...' : 'Ara'}
        </button>
      </SearchBox>

      {searchLoading && (
        <div style={{ textAlign: 'center', color: 'var(--secondary-color)', marginBottom: '20px' }}>
          Araması yapılıyor...
        </div>
      )}

      <ResultsContainer>
        {results.map(item => {
          const mediaTypeInfo = getMediaTypeInfo(item.detected_media_type);
          return (
            <ResultCard key={item.id}>
                <img 
                  src={getImageUrl(item.poster_path, 'w300')} 
                  alt={item.title || item.name} 
                  onError={(e) => {
                    e.target.src = '/placeholder-image.jpg';
                  }}
                />
              <div className="info">
                <h4>{item.display_title || item.title || item.name}</h4>
                <div className="media-type" style={{ color: mediaTypeInfo.color }}>
                  <span>{mediaTypeInfo.icon}</span>
                  <span>{mediaTypeInfo.label}</span>
                </div>
                <button 
                  onClick={() => handleAddToSite(item)} 
                  disabled={addingIds.includes(item.id)}
                >
                  {addingIds.includes(item.id) ? 'Ekleniyor...' : 'Siteye Ekle'}
                </button>
              </div>
            </ResultCard>
          );
        })}
      </ResultsContainer>

      {results.length === 0 && !searchLoading && (
        <div style={{ textAlign: 'center', color: 'var(--secondary-color)', marginTop: '50px' }}>
          Arama yapmak için yukarıdaki kutuya film, dizi veya anime adı yazın.
        </div>
      )}
    </AdminContainer>
  );
};

export default AdminAddPage;
